import { useEffect, useState } from "react";
import Link from "next/link";

import useStore from "@store/store";
import {
  selectAccountData,
  selectCreditBalance,
} from "@store/slices/accountSlice";

import QueryBuilder from "../Query/QueryBuilder";
import Archive from "../Archive/Archive";
import HelpTab from "../Help/HelpTab";
import Profile from "../Profile/Profile";
import LeftPanel from "./LeftPanel";
import { useUser } from "../../utils/AuthContext";
import VbaBuilder from "@components/VbaBuilder/VbaBuilder";
import TaskBuilder from "@components/TaskBuilder/TaskBuilder";
import { useRouter } from "next/router";

type Props = {
  isLoading: boolean;
};

const links = [
  "smart_toy",
  "code",
  "headset_mic",
  "archive",
  "help",
  "account_circle",
];

const UserDashboard = ({ isLoading }: Props) => {
  const { user, logOut } = useUser();
  const creditBalance = useStore(selectCreditBalance);
  const accountData = useStore(selectAccountData);
  const [tab, setTab] = useState(0);

  const router = useRouter();

  const tabQueryParam = Array.isArray(router.query.tabQueryParam) 
    ? router.query.tabQueryParam[0] 
    : router.query.tabQueryParam;

const tabNumber = tabQueryParam ? parseInt(tabQueryParam, 10) : 0;

  useEffect(() => {
    if (tabNumber) {
      setTab(tabNumber);
    }
  }, [tabNumber]);

  return (
    <>
      {creditBalance === 0 && accountData?.isPremuim === false && (
        <div className="h-8 bg-error text-error-content text-center">
          <a className="font-semibold cursor-pointer" onClick={() => setTab(3)}>
            0 Credits left. Upgrade to premium to get unlimited access!
          </a>
        </div>
      )}

      <section className="flex">
        <div className="hidden lg:block">
          <LeftPanel user={user} tab={tab} setTab={setTab} />
        </div>

        <div className="btm-nav z-40 h-16 lg:hidden">
          {links.map((link, i) => (
            <button
              key={i}
              onClick={() => setTab(i)}
              className={`w-[14%] ${tab === i ? "active bg-green-300" : ""}`}
            >
              <a className={`rounded-none`}>
                <span className="material-icons">{link}</span>
              </a>
            </button>
          ))}

          {user && (
            <>
              <button className="w-[14%]">
                <Link href={"/"}>
                  <a className={"rounded-none"}>
                    <span className="material-icons">home</span>
                  </a>
                </Link>
              </button>
              <button className="w-[14%]">
                <a className={"rounded-none"} onClick={() => logOut()}>
                  <span className="material-icons">logout</span>
                </a>
              </button>
            </>
          )}
        </div>

        {!isLoading ? (
          <div className="h-screen w-full bg-gray-100 scrollbar-thin scrollbar-thumb-gray-500 overflow-y-scroll overflow-x-hidden scrollbar-thumb-rounded-full scrollbar-track-rounded-full px-8 pb-20 lg:pb-0 lg:px-0">
            {tab === 0 && <QueryBuilder />}
            {tab === 1 && <VbaBuilder />}
            {tab === 2 && <TaskBuilder />}
            {tab === 3 && <Archive />}
            {tab === 4 && <HelpTab />}
            {tab === 5 && <Profile />}
          </div>
        ) : (
          <div role="status" className="max-w-sm animate-pulse">
            <div className="h-4 bg-base-200 rounded-full w-96 my-4"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full my-2.5"></div>
            <div className="h-2 bg-base-200 rounded-full max-w-[330px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[300px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px]"></div>
            <div className="h-4 bg-base-200 rounded-full w-96 my-4"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full my-2.5"></div>
            <div className="h-2 bg-base-200 rounded-full max-w-[330px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[300px] my-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px]"></div>
            <div className="h-4 bg-base-200 rounded-full w-96 my-4"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full mb-2.5"></div>
            <div className="h-2 bg-base-200 rounded-full max-w-[330px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[300px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px]"></div>
            <div className="h-3 bg-base-200 rounded-full mb-2.5"></div>
            <div className="h-2 bg-base-200 rounded-full max-w-[330px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[300px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px]"></div>
            <div className="h-3 bg-base-200 rounded-full mb-2.5"></div>
            <div className="h-2 bg-base-200 rounded-full max-w-[330px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[300px] mb-2.5"></div>
            <div className="h-3 bg-base-200 rounded-full max-w-[360px]"></div>
            <span className="sr-only">Loading...</span>
          </div>
        )}
      </section>
    </>
  );
};

export default UserDashboard;
